import * as React from "react";
import { Button, Form, Label, Modal, Tab, TabProps } from "semantic-ui-react";

import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";
import { getAnswerToQuestionWithId, UpdateAnswer } from "@teamprevent/common/questionnaires/getAnswerToQuestionWithId";
import { MultiButtonQuestion, NoOption, YesOption } from "@teamprevent/common/questionnaires/components/questions/MultiButtonQuestion";
import { AnsweredQuestion, AnsweredQuestionWithId  } from "@teamprevent/common/model";
import { MasterQuestionConfig } from "@teamprevent/common/questionnaires/questionnaireConfig/models";

import { OhCase } from "../../crud/ohCase";
import { ConsultationSectionIds, getQuestionConfig } from "../models";
import * as questionIds from "../mhpTreatmentConsultationQuestionIds";
import { iaptQuestionnaireAnswerOptions, iaptQuestionnaireAnswers, iaptQuestionnaireWsasAnswerOptions } from "../../clinicalNotes/models";
import { isGAD7Question, isIAPTGeneralQuestionsQuestion, isPHQ9Question, isWSASQuestion } from "../mhpTreatmentConsultationQuestionIds";
import { mhpConsultationMasterConfig } from "../mhpTreatmentConsultationMasterConfig";

interface Props {
    sectionId: string;
    readonly: boolean;
    ohCase: OhCase;
    questions: AnsweredQuestionWithId[];
    updateAnswer: (updatedQuestion: UpdateAnswer) => void;
}

enum IaptTab {
    PersonalInformation = 0,
    PHQ9 = 1,
    GAD7 = 2,
    WSAS = 3
}

export const IAPTQuestionnaire: React.FunctionComponent<Props> = (props: Props) => {

    const [iaptQuestionnaireModalOpen, setIaptQuestionnaireModalOpen] = React.useState<boolean>(false);
    const [iaptModalActiveIndex, setIaptModalActiveIndex] = React.useState<number>(0);
    const [psychologicalWellbeingPHQ9Score, setPsychologicalWellbeingPHQ9Score] = React.useState<number>(0);
    const [psychologicalWellbeingGAD7Score, setPsychologicalWellbeingGAD7Score] = React.useState<number>(0);
    const [psychologicalWellbeingWSASScore, setPsychologicalWellbeingWSASScore] = React.useState<number>(0);
    const [iaptQuestionnaireModalSubmitted, setIaptQuestionnaireModalSubmitted] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (props.readonly) {
            const psychologicalWellbeingPHQ9ScoreAnswer = getAnswerToQuestionWithId(props.questions, questionIds.PsychologicalWellbeingPHQ9Score) as number;
            const psychologicalWellbeingGAD7ScoreAnswer = getAnswerToQuestionWithId(props.questions, questionIds.PsychologicalWellbeingGAD7Score) as number;
            const psychologicalWellbeingWSASScoreAnswer = getAnswerToQuestionWithId(props.questions, questionIds.PsychologicalWellbeingWSASScore) as number;

            setPsychologicalWellbeingPHQ9Score(psychologicalWellbeingPHQ9ScoreAnswer);
            setPsychologicalWellbeingGAD7Score(psychologicalWellbeingGAD7ScoreAnswer);
            setPsychologicalWellbeingWSASScore(psychologicalWellbeingWSASScoreAnswer);
        }
    }, [props.readonly, props.questions]);

    const openIAPTQuestionnaire = () => {
        setIaptQuestionnaireModalOpen(true);
        setIaptQuestionnaireModalSubmitted(false);

        updatePhq9Score();
        updateGad7Score();
        updateWsasScore();

        const iaptCarryOutPhq9Assessment = getAnswerToQuestionWithId(props.questions, questionIds.IaptCarryOutPhq9Assessment) === YesOption;
        const iaptCarryOutGad7Assessment = getAnswerToQuestionWithId(props.questions, questionIds.IaptCarryOutGad7Assessment) === YesOption;
        const iaptCarryOutWsasAssessment = getAnswerToQuestionWithId(props.questions, questionIds.IaptCarryOutWsasAssessment) === YesOption;

        if (!iaptCarryOutPhq9Assessment) {

            const iaptCarryOutPhq9AssessmentQuestion: AnsweredQuestion = {
                id: questionIds.IaptCarryOutPhq9Assessment,
                answer: NoOption,
                question: (getQuestionConfig(questionIds.IaptCarryOutPhq9Assessment) as MasterQuestionConfig).questionText,
                isValid: true,
                sectionId: ConsultationSectionIds.IaptQuestionnaire
            };

            props.updateAnswer({
                sectionId: ConsultationSectionIds.IaptQuestionnaire,
                id: questionIds.IaptCarryOutPhq9Assessment,
                question: iaptCarryOutPhq9AssessmentQuestion
            });
        }

        if (!iaptCarryOutGad7Assessment) {

            const iaptCarryOutGad7AssessmentQuestion: AnsweredQuestion = {
                id: questionIds.IaptCarryOutGad7Assessment,
                answer: NoOption,
                question: (getQuestionConfig(questionIds.IaptCarryOutGad7Assessment) as MasterQuestionConfig).questionText,
                isValid: true,
                sectionId: ConsultationSectionIds.IaptQuestionnaire
            };

            props.updateAnswer({
                sectionId: ConsultationSectionIds.IaptQuestionnaire,
                id: questionIds.IaptCarryOutGad7Assessment,
                question: iaptCarryOutGad7AssessmentQuestion
            });
        }

        if (!iaptCarryOutWsasAssessment) {

            const iaptCarryOutWsasAssessmentQuestion: AnsweredQuestion = {
                id: questionIds.IaptCarryOutWsasAssessment,
                answer: NoOption,
                question: (getQuestionConfig(questionIds.IaptCarryOutWsasAssessment) as MasterQuestionConfig).questionText,
                isValid: true,
                sectionId: ConsultationSectionIds.IaptQuestionnaire
            };

            props.updateAnswer({
                sectionId: ConsultationSectionIds.IaptQuestionnaire,
                id: questionIds.IaptCarryOutWsasAssessment,
                question: iaptCarryOutWsasAssessmentQuestion
            });
        }
    };

    const isAnyQuestionInvalid = (ids: string[]) => {
        return ids.some(id => {
            const question = props.questions.find(q => q.id === id);
            return !question || !question.isValid;
        });
    };

    const setScoreAsNull = (scoreQuestionId: string) => {
        const scoreQuestion = props.questions.find(q => q.id === scoreQuestionId);

        props.updateAnswer({
            sectionId: props.sectionId,
            id: scoreQuestionId,
            question: { ...scoreQuestion, answer: null, isValid: false }
        });
    };

    const submitIAPTQuestionnaire = () => {
        setIaptQuestionnaireModalSubmitted(true);

        const iaptPersonalInformationQuestions = props.questions.filter(q => isIAPTGeneralQuestionsQuestion(q.id));
        if (iaptPersonalInformationQuestions.some(q => !q.isValid)) {
            onTabChange(IaptTab.PersonalInformation);
            return;
        }

        const iaptCarryOutPhq9Assessment = getAnswerToQuestionWithId(props.questions, questionIds.IaptCarryOutPhq9Assessment) === YesOption;
        const iaptCarryOutGad7Assessment = getAnswerToQuestionWithId(props.questions, questionIds.IaptCarryOutGad7Assessment) === YesOption;
        const iaptCarryOutWsasAssessment = getAnswerToQuestionWithId(props.questions, questionIds.IaptCarryOutWsasAssessment) === YesOption;

        if (iaptCarryOutPhq9Assessment) {
            const phq9QuestionIds = mhpConsultationMasterConfig.filter(q => isPHQ9Question(q.id)).map(q => q.id);
            if (isAnyQuestionInvalid(phq9QuestionIds)) {
                onTabChange(IaptTab.PHQ9);
                return;
            }

            updatePhq9Score();
        } else {
            setScoreAsNull(questionIds.PsychologicalWellbeingPHQ9Score);
        }

        if (iaptCarryOutGad7Assessment) {
            const gad7QuestionIds = mhpConsultationMasterConfig.filter(q => isGAD7Question(q.id)).map(q => q.id);
            if (isAnyQuestionInvalid(gad7QuestionIds)) {
                onTabChange(IaptTab.GAD7);
                return;
            }

            updateGad7Score();
        } else {
            setScoreAsNull(questionIds.PsychologicalWellbeingGAD7Score);
        }

        if (iaptCarryOutWsasAssessment) {
            const wsasQuestionIds = mhpConsultationMasterConfig.filter(q => isWSASQuestion(q.id)).map(q => q.id);
            if (isAnyQuestionInvalid(wsasQuestionIds)) {
                onTabChange(IaptTab.WSAS);
                return;
            }

            updateWsasScore();

        } else {
            setScoreAsNull(questionIds.PsychologicalWellbeingWSASScore);
        }

        const iaptCarryOutIaptAssessmentQuestion: AnsweredQuestion = {
            id: questionIds.IaptCarryOutIaptAssessment,
            answer: YesOption,
            question: questionIds.IaptCarryOutIaptAssessment,
            isValid: true,
            sectionId: ConsultationSectionIds.IaptQuestionnaire
        };

        props.updateAnswer({
            sectionId: ConsultationSectionIds.IaptQuestionnaire,
            id: questionIds.IaptCarryOutIaptAssessment,
            question: iaptCarryOutIaptAssessmentQuestion
        });

        setIaptQuestionnaireModalOpen(false);
    };

    const closeQuestionnaire = () => {
        setScoreAsNull(questionIds.PsychologicalWellbeingPHQ9Score);
        setScoreAsNull(questionIds.PsychologicalWellbeingGAD7Score);
        setScoreAsNull(questionIds.PsychologicalWellbeingWSASScore);

        setIaptQuestionnaireModalSubmitted(false);
        setIaptQuestionnaireModalOpen(false);
    };

    const handleTabChange = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, data: TabProps) => {
        const index = Number(data.activeIndex);
        setIaptModalActiveIndex(index);
    };

    const updateIaptAnswer = (questionId: string, question: AnsweredQuestion) => {
        if (props.readonly) {
            return;
        }

        if (isPHQ9Question(questionId)) {
            updatePhq9Score(questionId, question.answer?.toString());
        }

        if (isGAD7Question(questionId)) {
            updateGad7Score(questionId, question.answer?.toString());
        }

        if (isWSASQuestion(questionId)) {
            updateWsasScore(questionId, question.answer?.toString());
        }

        props.updateAnswer({
            sectionId: ConsultationSectionIds.IaptQuestionnaire,
            id: questionId,
            question
        });
    };

    const updatePhq9Score = (currentQuestionId?: string, currentAnswer?: string) => {
        if (props.readonly) {
            return;
        }

        let phq9Answers = props.questions.filter(q => isPHQ9Question(q.id));

        if (currentQuestionId) {
            phq9Answers = phq9Answers.filter(q => q.id !== currentQuestionId);
        }

        let phq9Score = phq9Answers
            .map(q => iaptQuestionnaireAnswers.find(a => a.text === q.answer?.toString())?.score)
            .reduce((sum, current) => sum + (!!current && !isNaN(current) ? current : 0), 0);

        if (currentAnswer) {
            const currentQuestionScore = iaptQuestionnaireAnswers.find(a => a.text === currentAnswer)?.score;

            phq9Score = (!!currentQuestionScore && !isNaN(currentQuestionScore) ? currentQuestionScore : 0) + phq9Score;
        }

        const phq9TotalQuestionId = questionIds.PsychologicalWellbeingPHQ9Score;
        const phq9TotalQuestion = props.questions.find(q => q.id === phq9TotalQuestionId);

        props.updateAnswer({
            sectionId: props.sectionId,
            id: phq9TotalQuestionId,
            question: { ...phq9TotalQuestion, answer: phq9Score }
        });

        setPsychologicalWellbeingPHQ9Score(phq9Score);
    };

    const updateGad7Score = (currentQuestionId?: string, currentAnswer?: string) => {
        if (props.readonly) {
            return;
        }

        let gad7Answers = props.questions.filter(q => isGAD7Question(q.id));

        if (currentQuestionId) {
            gad7Answers = gad7Answers.filter(q => q.id !== currentQuestionId);
        }

        let gad7Score = gad7Answers
            .map(q => iaptQuestionnaireAnswers.find(a => a.text === q.answer?.toString())?.score)
            .reduce((sum, current) => sum + (!!current && !isNaN(current) ? current : 0), 0);

        if (currentAnswer) {
            const currentQuestionScore = iaptQuestionnaireAnswers.find(a => a.text === currentAnswer)?.score;

            gad7Score = (!!currentQuestionScore && !isNaN(currentQuestionScore) ? currentQuestionScore : 0) + gad7Score;
        }

        const gad7TotalQuestionId = questionIds.PsychologicalWellbeingGAD7Score;
        const gad7TotalQuestion = props.questions.find(q => q.id === gad7TotalQuestionId);

        props.updateAnswer({
            sectionId: props.sectionId,
            id: gad7TotalQuestionId,
            question: { ...gad7TotalQuestion, answer: gad7Score }
        });

        setPsychologicalWellbeingGAD7Score(gad7Score);
    };

    const updateWsasScore = (currentQuestionId?: string, currentAnswer?: string) => {
        if (props.readonly) {
            return;
        }

        let wsasAnswers = props.questions.filter(q => isWSASQuestion(q.id));

        if (currentQuestionId) {
            wsasAnswers = wsasAnswers.filter(q => q.id !== currentQuestionId);
        }

        let wsasScore = wsasAnswers
            .map(q => q.answer?.toString() ? Number(q.answer?.toString()) : 0)
            .reduce((sum, current) => sum + (!!current && !isNaN(current) ? current : 0), 0);

        if (currentAnswer) {
            const currentQuestionScore = Number(currentAnswer);

            wsasScore = (!!currentQuestionScore && !isNaN(currentQuestionScore) ? currentQuestionScore : 0) + wsasScore;
        }

        const wsasTotalQuestionId = questionIds.PsychologicalWellbeingWSASScore;
        const wsasTotalQuestion = props.questions.find(q => q.id === wsasTotalQuestionId);

        props.updateAnswer({
            sectionId: props.sectionId,
            id: wsasTotalQuestionId,
            question: { ...wsasTotalQuestion, answer: wsasScore }
        });

        setPsychologicalWellbeingWSASScore(wsasScore);
    };

    const onTabChange = (tabIndex: number) => {
        setIaptModalActiveIndex(tabIndex);
    };

    const getOpenModalButtonDescription = (): string => {
        if (props.readonly) {
            return "View IAPT Questionnaire";
        }

        return props.questions.some(q => q.sectionId === ConsultationSectionIds.IaptQuestionnaire)
            ? "Edit IAPT Questionnaire"
            : "Complete IAPT Questionnaire";
    };

    const iaptModalTabs = [
        {
            menuItem: "Personal Information",
            render: () => {
                return (
                    <Tab.Pane attached={false}>
                        <TextQuestion
                            inline
                            hideDivider
                            readonly={true}
                            questionConfig={getQuestionConfig(questionIds.IaptEmployeeName)}
                            updateQuestion={(question) => props.updateAnswer({
                                sectionId: ConsultationSectionIds.IaptQuestionnaire,
                                id: questionIds.IaptEmployeeName,
                                question
                            })}
                            answer={`${props.ohCase.employeeFirstName} ${props.ohCase.employeeLastName}`}
                        />
                        <TextQuestion
                            inline
                            hideDivider
                            readonly={true}
                            questionConfig={getQuestionConfig(questionIds.IaptJobRole)}
                            updateQuestion={(question) => props.updateAnswer({
                                sectionId: ConsultationSectionIds.IaptQuestionnaire,
                                id: questionIds.IaptJobRole,
                                question
                            })}
                            answer={props.ohCase.jobTitle}
                        />
                        <TextQuestion
                            readonly={true}
                            hidden={true}
                            questionConfig={getQuestionConfig(questionIds.IaptCarryOutIaptAssessment)}
                            answer={YesOption}
                        />
                        <br />
                        <Button type="button" content="Next" onClick={() => onTabChange(IaptTab.PHQ9)} />
                    </Tab.Pane>
                );
            }
        },
        {
            menuItem: "PHQ9",
            render: () => {
                return (
                    <Tab.Pane attached={false}>
                        <MultiButtonQuestion
                            hideDivider
                            inline
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(questionIds.IaptCarryOutPhq9Assessment)}
                            mandatory
                            updateQuestion={(question) => props.updateAnswer({
                                sectionId: ConsultationSectionIds.IaptQuestionnaire,
                                id: questionIds.IaptCarryOutPhq9Assessment,
                                question
                            })}
                            answer={getAnswerToQuestionWithId(props.questions, questionIds.IaptCarryOutPhq9Assessment)}
                            showError={iaptQuestionnaireModalSubmitted}
                        />
                        {getAnswerToQuestionWithId(props.questions, questionIds.IaptCarryOutPhq9Assessment) === YesOption &&
                            <>
                                <h4>Over the last 2 weeks, how often have you been bothered by any of the following problems?</h4>
                                <MultiButtonQuestion
                                    hideDivider
                                    inline
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(questionIds.IaptLittleInterestOrPleasureInDoingThings)}
                                    mandatory
                                    values={iaptQuestionnaireAnswerOptions}
                                    updateQuestion={(question) => updateIaptAnswer(
                                        questionIds.IaptLittleInterestOrPleasureInDoingThings,
                                        question)}
                                    answer={getAnswerToQuestionWithId(props.questions, questionIds.IaptLittleInterestOrPleasureInDoingThings)}
                                    showError={iaptQuestionnaireModalSubmitted}
                                />
                                <MultiButtonQuestion
                                    hideDivider
                                    inline
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(questionIds.IaptFeelingDownDepressedOrHopeless)}
                                    mandatory
                                    values={iaptQuestionnaireAnswerOptions}
                                    updateQuestion={(question) => updateIaptAnswer(
                                        questionIds.IaptFeelingDownDepressedOrHopeless,
                                        question)}
                                    answer={getAnswerToQuestionWithId(props.questions, questionIds.IaptFeelingDownDepressedOrHopeless)}
                                    showError={iaptQuestionnaireModalSubmitted}
                                />
                                <MultiButtonQuestion
                                    hideDivider
                                    inline
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(questionIds.IaptTroubleFallingOrStayingAsleepOrSleepingTooMuch)}
                                    mandatory
                                    values={iaptQuestionnaireAnswerOptions}
                                    updateQuestion={(question) => updateIaptAnswer(
                                        questionIds.IaptTroubleFallingOrStayingAsleepOrSleepingTooMuch,
                                        question)}
                                    answer={getAnswerToQuestionWithId(
                                        props.questions,
                                        questionIds.IaptTroubleFallingOrStayingAsleepOrSleepingTooMuch)}
                                    showError={iaptQuestionnaireModalSubmitted}
                                />
                                <MultiButtonQuestion
                                    hideDivider
                                    inline
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(questionIds.IaptFeelingTiredOrHavingLittleEnergy)}
                                    mandatory
                                    values={iaptQuestionnaireAnswerOptions}
                                    updateQuestion={(question) => updateIaptAnswer(
                                        questionIds.IaptFeelingTiredOrHavingLittleEnergy,
                                        question)}
                                    answer={getAnswerToQuestionWithId(props.questions, questionIds.IaptFeelingTiredOrHavingLittleEnergy)}
                                    showError={iaptQuestionnaireModalSubmitted}
                                />
                                <MultiButtonQuestion
                                    hideDivider
                                    inline
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(questionIds.IaptPoorAppetiteOrOvereating)}
                                    mandatory
                                    values={iaptQuestionnaireAnswerOptions}
                                    updateQuestion={(question) => updateIaptAnswer(
                                        questionIds.IaptPoorAppetiteOrOvereating,
                                        question)}
                                    answer={getAnswerToQuestionWithId(props.questions, questionIds.IaptPoorAppetiteOrOvereating)}
                                    showError={iaptQuestionnaireModalSubmitted}
                                />
                                <MultiButtonQuestion
                                    hideDivider
                                    inline
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(questionIds.IaptFeelingBadAboutYourself)}
                                    mandatory
                                    values={iaptQuestionnaireAnswerOptions}
                                    updateQuestion={(question) => updateIaptAnswer(
                                        questionIds.IaptFeelingBadAboutYourself,
                                        question)}
                                    answer={getAnswerToQuestionWithId(props.questions, questionIds.IaptFeelingBadAboutYourself)}
                                    showError={iaptQuestionnaireModalSubmitted}
                                />
                                <MultiButtonQuestion
                                    hideDivider
                                    inline
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(questionIds.IaptTroubleConcentratingOnReadingOrWatching)}
                                    mandatory
                                    values={iaptQuestionnaireAnswerOptions}
                                    updateQuestion={(question) => updateIaptAnswer(
                                        questionIds.IaptTroubleConcentratingOnReadingOrWatching,
                                        question)}
                                    answer={getAnswerToQuestionWithId(
                                        props.questions,
                                        questionIds.IaptTroubleConcentratingOnReadingOrWatching)}
                                    showError={iaptQuestionnaireModalSubmitted}
                                />
                                <MultiButtonQuestion
                                    hideDivider
                                    inline
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(questionIds.IaptMovingOrSpeakingSoSlowly)}
                                    mandatory
                                    values={iaptQuestionnaireAnswerOptions}
                                    updateQuestion={(question) => updateIaptAnswer(
                                        questionIds.IaptMovingOrSpeakingSoSlowly,
                                        question)}
                                    answer={getAnswerToQuestionWithId(props.questions, questionIds.IaptMovingOrSpeakingSoSlowly)}
                                    showError={iaptQuestionnaireModalSubmitted}
                                />
                                <MultiButtonQuestion
                                    inline
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(questionIds.IaptThoughtsThatWouldBeBetterOffDeadOrOfHurtingYourself)}
                                    mandatory
                                    values={iaptQuestionnaireAnswerOptions}
                                    updateQuestion={(question) => updateIaptAnswer(
                                        questionIds.IaptThoughtsThatWouldBeBetterOffDeadOrOfHurtingYourself,
                                        question)}
                                    answer={getAnswerToQuestionWithId(
                                        props.questions,
                                        questionIds.IaptThoughtsThatWouldBeBetterOffDeadOrOfHurtingYourself)}
                                    showError={iaptQuestionnaireModalSubmitted}
                                />
                                <TextQuestion
                                    hideDivider
                                    inline
                                    readonly={true}
                                    questionConfig={getQuestionConfig(questionIds.IaptPsychologicalWellbeingPHQ9Score)}
                                    questionClassName={"question-red"}
                                    updateQuestion={(question) => updateIaptAnswer(
                                        questionIds.IaptPsychologicalWellbeingPHQ9Score,
                                        question)}
                                    answer={psychologicalWellbeingPHQ9Score}
                                />
                            </>
                        }
                        <br />
                        <Button type="button" content="Next" onClick={() => onTabChange(IaptTab.GAD7)} />
                    </Tab.Pane>
                );
            }
        },
        {
            menuItem: "GAD7",
            render: () => {
                return (
                    <Tab.Pane attached={false}>
                        <MultiButtonQuestion
                            hideDivider
                            inline
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(questionIds.IaptCarryOutGad7Assessment)}
                            mandatory
                            updateQuestion={(question) => props.updateAnswer({
                                sectionId: ConsultationSectionIds.IaptQuestionnaire,
                                id: questionIds.IaptCarryOutGad7Assessment,
                                question
                            })}
                            answer={getAnswerToQuestionWithId(props.questions, questionIds.IaptCarryOutGad7Assessment)}
                            showError={iaptQuestionnaireModalSubmitted}
                        />
                        {getAnswerToQuestionWithId(props.questions, questionIds.IaptCarryOutGad7Assessment) === YesOption &&
                            <>
                                <h4>Over the last 2 weeks, how often have you been bothered by any of the following problems?</h4>
                                <MultiButtonQuestion
                                    hideDivider
                                    inline
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(questionIds.IaptFeelingNervousAnxiousOrOnEdge)}
                                    mandatory
                                    values={iaptQuestionnaireAnswerOptions}
                                    updateQuestion={(question) => updateIaptAnswer(
                                        questionIds.IaptFeelingNervousAnxiousOrOnEdge,
                                        question)}
                                    answer={getAnswerToQuestionWithId(props.questions, questionIds.IaptFeelingNervousAnxiousOrOnEdge)}
                                    showError={iaptQuestionnaireModalSubmitted}
                                />
                                <MultiButtonQuestion
                                    hideDivider
                                    inline
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(questionIds.IaptNotBeingAbleToStopOrControlWorrying)}
                                    mandatory
                                    values={iaptQuestionnaireAnswerOptions}
                                    updateQuestion={(question) => updateIaptAnswer(
                                        questionIds.IaptNotBeingAbleToStopOrControlWorrying,
                                        question)}
                                    answer={getAnswerToQuestionWithId(props.questions, questionIds.IaptNotBeingAbleToStopOrControlWorrying)}
                                    showError={iaptQuestionnaireModalSubmitted}
                                />
                                <MultiButtonQuestion
                                    hideDivider
                                    inline
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(questionIds.IaptWorryingTooMuchAboutDifferentThings)}
                                    mandatory
                                    values={iaptQuestionnaireAnswerOptions}
                                    updateQuestion={(question) => updateIaptAnswer(
                                        questionIds.IaptWorryingTooMuchAboutDifferentThings,
                                        question)}
                                    answer={getAnswerToQuestionWithId(props.questions, questionIds.IaptWorryingTooMuchAboutDifferentThings)}
                                    showError={iaptQuestionnaireModalSubmitted}
                                />
                                <MultiButtonQuestion
                                    hideDivider
                                    inline
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(questionIds.IaptTroubleRelaxing)}
                                    mandatory
                                    values={iaptQuestionnaireAnswerOptions}
                                    updateQuestion={(question) => updateIaptAnswer(
                                        questionIds.IaptTroubleRelaxing,
                                        question)}
                                    answer={getAnswerToQuestionWithId(props.questions, questionIds.IaptTroubleRelaxing)}
                                    showError={iaptQuestionnaireModalSubmitted}
                                />
                                <MultiButtonQuestion
                                    hideDivider
                                    inline
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(questionIds.IaptBeingSoRestlessThatItIsHardToSitStill)}
                                    mandatory
                                    values={iaptQuestionnaireAnswerOptions}
                                    updateQuestion={(question) => updateIaptAnswer(
                                        questionIds.IaptBeingSoRestlessThatItIsHardToSitStill,
                                        question)}
                                    answer={getAnswerToQuestionWithId(props.questions, questionIds.IaptBeingSoRestlessThatItIsHardToSitStill)}
                                    showError={iaptQuestionnaireModalSubmitted}
                                />
                                <MultiButtonQuestion
                                    hideDivider
                                    inline
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(questionIds.IaptBecomingEasilyAnnoyedOrIrritable)}
                                    mandatory
                                    values={iaptQuestionnaireAnswerOptions}
                                    updateQuestion={(question) => updateIaptAnswer(
                                        questionIds.IaptBecomingEasilyAnnoyedOrIrritable,
                                        question)}
                                    answer={getAnswerToQuestionWithId(
                                        props.questions,
                                        questionIds.IaptBecomingEasilyAnnoyedOrIrritable)}
                                    showError={iaptQuestionnaireModalSubmitted}
                                />
                                <MultiButtonQuestion
                                    inline
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(questionIds.IaptFeelingAfraidAsIfSomethingAwfulMightHappen)}
                                    mandatory
                                    values={iaptQuestionnaireAnswerOptions}
                                    updateQuestion={(question) => updateIaptAnswer(
                                        questionIds.IaptFeelingAfraidAsIfSomethingAwfulMightHappen,
                                        question)}
                                    answer={getAnswerToQuestionWithId(
                                        props.questions,
                                        questionIds.IaptFeelingAfraidAsIfSomethingAwfulMightHappen)}
                                    showError={iaptQuestionnaireModalSubmitted}
                                />
                                <TextQuestion
                                    hideDivider
                                    inline
                                    readonly={true}
                                    questionConfig={getQuestionConfig(questionIds.IaptPsychologicalWellbeingGAD7Score)}
                                    questionClassName={"question-red"}
                                    updateQuestion={(question) => updateIaptAnswer(
                                        questionIds.IaptPsychologicalWellbeingGAD7Score,
                                        question)}
                                    answer={psychologicalWellbeingGAD7Score}
                                />
                            </>
                        }
                        <br />
                        <Button type="button" content="Next" onClick={() => onTabChange(IaptTab.WSAS)} />
                    </Tab.Pane>
                );
            }
        },
        {
            menuItem: "WSAS",
            render: () => {
                return (
                    <Tab.Pane attached={false}>
                        <MultiButtonQuestion
                            hideDivider
                            inline
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(questionIds.IaptCarryOutWsasAssessment)}
                            mandatory
                            updateQuestion={(question) => props.updateAnswer({
                                sectionId: ConsultationSectionIds.IaptQuestionnaire,
                                id: questionIds.IaptCarryOutWsasAssessment,
                                question
                            })}
                            answer={getAnswerToQuestionWithId(props.questions, questionIds.IaptCarryOutWsasAssessment)}
                            showError={iaptQuestionnaireModalSubmitted}
                        />
                        {getAnswerToQuestionWithId(props.questions, questionIds.IaptCarryOutWsasAssessment) === YesOption &&
                            <>
                                <h4>People’s problems sometimes affect their ability to do certain day-to-day tasks in their lives.
                                    To rate your problems, look at each section and determine on the scale provided how much your problem impairs
                                    your ability to carry out the activity.
                                </h4>
                                {!props.readonly &&
                                    <>
                                        <h4> Scale: 0 - Not at all, 2 - Slightly, 4 - Definitely, 6 - Markedly, Severely 8</h4>
                                        <br />
                                    </>
                                }
                                <MultiButtonQuestion
                                    hideDivider
                                    inline
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(questionIds.IaptWork)}
                                    mandatory
                                    values={iaptQuestionnaireWsasAnswerOptions}
                                    updateQuestion={(question) => updateIaptAnswer(
                                        questionIds.IaptWork,
                                        question)}
                                    answer={getAnswerToQuestionWithId(props.questions, questionIds.IaptWork)}
                                    showError={iaptQuestionnaireModalSubmitted}
                                />
                                <MultiButtonQuestion
                                    hideDivider
                                    inline
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(questionIds.IaptHomeManagement)}
                                    mandatory
                                    values={iaptQuestionnaireWsasAnswerOptions}
                                    updateQuestion={(question) => updateIaptAnswer(
                                        questionIds.IaptHomeManagement,
                                        question)}
                                    answer={getAnswerToQuestionWithId(props.questions, questionIds.IaptHomeManagement)}
                                    showError={iaptQuestionnaireModalSubmitted}
                                />
                                <MultiButtonQuestion
                                    hideDivider
                                    inline
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(questionIds.IaptSocialLeisureActivities)}
                                    mandatory
                                    values={iaptQuestionnaireWsasAnswerOptions}
                                    updateQuestion={(question) => updateIaptAnswer(
                                        questionIds.IaptSocialLeisureActivities,
                                        question)}
                                    answer={getAnswerToQuestionWithId(props.questions, questionIds.IaptSocialLeisureActivities)}
                                    showError={iaptQuestionnaireModalSubmitted}
                                />
                                <MultiButtonQuestion
                                    hideDivider
                                    inline
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(questionIds.IaptPrivateLeisureActivities)}
                                    mandatory
                                    values={iaptQuestionnaireWsasAnswerOptions}
                                    updateQuestion={(question) => updateIaptAnswer(
                                        questionIds.IaptPrivateLeisureActivities,
                                        question)}
                                    answer={getAnswerToQuestionWithId(props.questions, questionIds.IaptPrivateLeisureActivities)}
                                    showError={iaptQuestionnaireModalSubmitted}
                                />
                                <MultiButtonQuestion
                                    inline
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(questionIds.IaptFamilyAndRelationships)}
                                    mandatory
                                    values={iaptQuestionnaireWsasAnswerOptions}
                                    updateQuestion={(question) => updateIaptAnswer(
                                        questionIds.IaptFamilyAndRelationships,
                                        question)}
                                    answer={getAnswerToQuestionWithId(props.questions, questionIds.IaptFamilyAndRelationships)}
                                    showError={iaptQuestionnaireModalSubmitted}
                                />
                                <TextQuestion
                                    hideDivider
                                    inline
                                    readonly={true}
                                    questionConfig={getQuestionConfig(questionIds.IaptPsychologicalWellbeingWSASScore)}
                                    questionClassName={"question-red"}
                                    updateQuestion={(question) => updateIaptAnswer(
                                        questionIds.IaptPsychologicalWellbeingWSASScore,
                                        question)}
                                    answer={psychologicalWellbeingWSASScore}
                                />
                            </>
                        }
                    </Tab.Pane>
                );
            }
        }
    ];

    return (
        <>
            <Modal open={iaptQuestionnaireModalOpen} size="large" closeOnDimmerClick={false}>
                <Modal.Header>IAPT Questionnaire</Modal.Header>
                <Modal.Content>
                    <Form>
                        <Tab
                            menu={{ secondary: true, pointing: true }}
                            panes={iaptModalTabs}
                            activeIndex={iaptModalActiveIndex}
                            onTabChange={handleTabChange}
                        />
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    {!props.readonly && <Button type="button" content="Submit" onClick={submitIAPTQuestionnaire} />}
                    <Button type="button" content="Close" onClick={closeQuestionnaire} />
                </Modal.Actions>
            </Modal>
            <Label><b>IAPT Section</b></Label>
            <br />
            <Button type="button" content={getOpenModalButtonDescription()} onClick={openIAPTQuestionnaire} />
        </>
    );
};
